import { useEffect, useMemo, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Trophy, Swords, Zap, Eye, Radio, Target } from 'lucide-react';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import ArenaxTrendingStrip from './ArenaxTrendingStrip';
import ArenaxRightNow from './ArenaxRightNow';
import ArenaxCityPulseTicker from './ArenaxCityPulseTicker';

/**
 * Phase 2L PR 2 — Citywide Live Feed page.
 *
 * Vertical scrolling stream of cinematic cards.  Sorted by NARRATIVE
 * significance on the backend.  Tone: live sports culture, not activity log.
 *
 * Card kinds: live, final, semifinal, comeback, rivalry_kickoff,
 * spectator_surge, streak_break, mvp_moment, standings_jump, match_completed.
 *
 * Auto-refreshes every 25s; preserves scroll position with a stable key.
 */
export default function ArenaxCityFeed() {
  const [items, setItems] = useState([]);
  const [feedHeader, setFeedHeader] = useState(null);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState('Bengaluru');
  const [sport, setSport] = useState('Football');
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    const fetchFeed = () => {
      const params = new URLSearchParams();
      if (city) params.set('city', city);
      if (sport) params.set('sport', sport);
      params.set('limit', '30');
      api.get(`/arenax/city-feed?${params.toString()}`)
        .then(r => {
          if (!isMountedRef.current) return;
          setItems(r.data?.items || []);
          setFeedHeader(r.data?.feed_header || null);
        })
        .catch(() => { if (isMountedRef.current) { setItems([]); setFeedHeader(null); } })
        .finally(() => { if (isMountedRef.current) setLoading(false); });
    };
    fetchFeed();
    const tid = setInterval(fetchFeed, 25000);
    return () => { isMountedRef.current = false; clearInterval(tid); };
  }, [city, sport]);

  const cityOptions = ['Bengaluru', 'Mumbai', 'Delhi', 'Pune'];
  const sportOptions = ['Football', 'Cricket', 'Badminton', 'Tennis'];

  return (
    <ArenaxLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 lg:py-12" data-testid="arenax-city-feed-page">
        {/* Hero — Phase 2O PR 1: editorial feed_header drives the voice when scope is set */}
        <div className="rounded-2xl border border-amber-500/15 p-6 mb-6"
          data-testid="arenax-city-feed-hero"
          style={{ background: 'var(--ax-card-gradient)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Radio className="h-4 w-4 text-amber-300 animate-pulse" />
            <span className="text-[10px] tracking-[0.3em] text-amber-300 font-black uppercase">
              {feedHeader?.scope?.sport && feedHeader?.scope?.city
                ? `Live · ${feedHeader.scope.city} ${feedHeader.scope.sport}`
                : feedHeader?.scope?.city
                  ? `Live · ${feedHeader.scope.city}`
                  : feedHeader?.scope?.sport
                    ? `Live · ${feedHeader.scope.sport}`
                    : 'Live · the city'}
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-100 leading-tight"
            data-testid="arenax-city-feed-title">
            {feedHeader?.title ? (
              <span className="arenax-hero-gradient-text">
                {feedHeader.title}
              </span>
            ) : (
              <span className="arenax-hero-gradient-text">
                The city heartbeat
              </span>
            )}
          </h1>
          <p className="text-sm text-slate-300 mt-2 max-w-xl"
            data-testid="arenax-city-feed-subtitle">
            {feedHeader?.subtitle
              || 'Rivalry nights, comebacks, last-minute winners, MVP moments — sorted by what matters, not by what just happened.'}
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <div className="flex items-center gap-1.5" data-testid="arenax-city-feed-city-filters">
            {cityOptions.map(c => (
              <button key={c} onClick={() => setCity(c)}
                className={`px-2.5 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${
                  city === c ? 'text-cyan-200 bg-cyan-500/15 border border-cyan-500/30' : 'text-slate-500 border border-slate-800 hover:text-slate-300'
                }`}>{c}</button>
            ))}
          </div>
          <div className="flex items-center gap-1.5" data-testid="arenax-city-feed-sport-filters">
            {sportOptions.map(s => (
              <button key={s} onClick={() => setSport(s)}
                className={`px-2.5 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${
                  sport === s ? 'text-pink-200 bg-pink-500/15 border border-pink-500/30' : 'text-slate-500 border border-slate-800 hover:text-slate-300'
                }`}>{s}</button>
            ))}
          </div>
        </div>

        {/* Phase 128 — Live in the City ambient ticker (booking-driven civic motion) */}
        <section className="mb-6" data-testid="arenax-city-pulse-section">
          <ArenaxCityPulseTicker city={city} maxItems={8} />
        </section>

        {/* Phase 2R-CityMoments — "Right Now in [City]" narrative significance hero */}
        <div className="mb-8">
          <ArenaxRightNow city={city} sport={sport} />
        </div>

        {/* Phase 2P — Tonight's Biggest Moments strip (velocity-ranked clips) */}
        <div className="mb-6">
          <ArenaxTrendingStrip city={city} sport={sport} />
        </div>

        {/* Feed */}
        {loading ? (
          <div className="space-y-3" data-testid="arenax-city-feed-loading">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-24 rounded-2xl border border-slate-800 animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-3" data-testid="arenax-city-feed-list">
            {items.map((it, idx) => (
              <FeedCard key={`${it.kind}-${it.match_id || it.tournament_id || it.squad_id || idx}-${idx}`}
                item={it} index={idx} />
            ))}
          </div>
        )}
      </div>
    </ArenaxLayout>
  );
}

const KIND_META = {
  live: { Icon: Flame, accent: 'text-amber-300', border: 'border-amber-500/40',
            bg: 'linear-gradient(135deg, rgba(245,158,11,0.16), var(--ax-card-bg-soft))', label: 'LIVE' },
  final: { Icon: Trophy, accent: 'text-amber-300', border: 'border-amber-500/40',
            bg: 'linear-gradient(135deg, rgba(245,158,11,0.14), var(--ax-card-bg-soft))', label: 'FINAL' },
  semifinal: { Icon: Swords, accent: 'text-rose-300', border: 'border-rose-500/35',
                bg: 'linear-gradient(135deg, rgba(244,63,94,0.14), var(--ax-card-bg-soft))', label: 'SEMI' },
  rivalry_kickoff: { Icon: Flame, accent: 'text-rose-300', border: 'border-rose-500/35',
                       bg: 'linear-gradient(135deg, rgba(244,63,94,0.14), var(--ax-card-bg-soft))', label: 'RIVALRY' },
  comeback: { Icon: Zap, accent: 'text-amber-200', border: 'border-amber-500/40',
                bg: 'linear-gradient(135deg, rgba(251,113,133,0.14), rgba(245,158,11,0.10), var(--ax-card-bg-soft))', label: 'COMEBACK' },
  spectator_surge: { Icon: Eye, accent: 'text-cyan-300', border: 'border-cyan-500/35',
                       bg: 'linear-gradient(135deg, rgba(8,145,178,0.16), var(--ax-card-bg-soft))', label: 'WATCH' },
  streak_break: { Icon: Swords, accent: 'text-rose-300', border: 'border-rose-500/35',
                    bg: 'linear-gradient(135deg, rgba(244,63,94,0.14), var(--ax-card-bg-soft))', label: 'STREAK BROKEN' },
  mvp_moment: { Icon: Target, accent: 'text-emerald-300', border: 'border-emerald-500/35',
                  bg: 'linear-gradient(135deg, rgba(16,185,129,0.14), var(--ax-card-bg-soft))', label: 'MVP' },
  standings_jump: { Icon: Zap, accent: 'text-violet-300', border: 'border-violet-500/35',
                     bg: 'linear-gradient(135deg, rgba(139,92,246,0.14), var(--ax-card-bg-soft))', label: 'CLIMBING' },
  match_completed: { Icon: Flame, accent: 'text-slate-300', border: 'border-slate-700',
                       bg: 'var(--ax-card-bg-soft)', label: 'RESULT' },
};

function FeedCard({ item, index }) {
  const meta = KIND_META[item.kind] || KIND_META.match_completed;
  const { Icon, accent, border, bg, label } = meta;
  return (
    <Link
      to={item.deeplink || '/arenax'}
      data-testid={`arenax-city-feed-card-${item.kind}-${item.match_id || item.tournament_id || item.squad_id || index}`}
      className={`block rounded-2xl border ${border} p-4 transition-all hover:translate-y-[-1px]`}
      style={{
        background: bg,
        animation: `arenax-feed-enter 400ms ease-out both`,
        animationDelay: `${Math.min(index * 30, 200)}ms`,
      }}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className={`h-3.5 w-3.5 ${accent} ${item.kind === 'live' ? 'animate-pulse' : ''}`} />
        <span className={`text-[9px] uppercase tracking-[0.25em] font-black ${accent}`}>
          {label}
        </span>
        {item.viewer_count >= 3 && (
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-cyan-200/85">
            <Eye className="h-3 w-3" />
            <span className="tabular-nums">{item.viewer_count}</span>
          </span>
        )}
        {item.hours_to_go != null && item.hours_to_go > 0 && (
          <span className="ml-auto text-[9px] uppercase tracking-wider text-slate-500 font-bold">
            in {item.hours_to_go < 1 ? '<1h' : `${Math.round(item.hours_to_go)}h`}
          </span>
        )}
      </div>
      <p className="text-sm sm:text-base leading-snug text-slate-100 font-bold">
        {item.narrative}
      </p>
      <style>{`
        @keyframes arenax-feed-enter {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Link>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-12 px-6 rounded-2xl border border-dashed border-slate-700/60"
      data-testid="arenax-city-feed-empty">
      <Radio className="mx-auto h-8 w-8 text-slate-500 mb-3" />
      <h3 className="text-lg font-black text-slate-100 mb-1">The city's quiet right now.</h3>
      <p className="text-sm text-slate-400">When matches kick off, rivalries rekindle or comebacks fire, this is where you'll feel it first.</p>
    </div>
  );
}
